import React, { useState, useEffect, useRef } from 'react';
import { trackStepStart } from '../utils/analytics';
import './RunbookStep.css';

function RunbookStep({ step, isActive, isCompleted, onStepComplete, setActiveStepId, onTimeUpdate }) {
  // We removed collapsible behavior; content always visible (2-row layout: header + body)
  const expanded = true; // kept for minimal downstream impact
  const [remainingTime, setRemainingTime] = useState(step.duration);
  const [timerRunning, setTimerRunning] = useState(false);
  const timerRef = useRef(null);
  
  // Format time from seconds to HH:MM:SS
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Play notification sound
  const playNotificationSound = () => {
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBhNhsMfepF0VBAcPRIiqs7KpmX1xaWx9lK2/uq6ekoJpTkE8TGuEmpmknpKRgGNVT0xFRlJgbXp/jqGnrKqnlnppW09TSFBbZnN7hpOZoaGkmPQHSklXcn2FkJeepK6usK2npp2Wj4V4aVtKPjQvKSYmLTc9QkZLUFZcY2hucXNwbGpqaGZjX1tYU09GQkA8NzU0MzY7QUVFRUZGRUVFRkdGR0dHR0dHSEhISUlJSkpLTExMTU5PTlBRUFBRT09PUFBPTk1OTk1MSkhGRkVCPz43NTUzMTAsLCsqKSclJCIiISAeHR0cGxsaGRkYGBcXFhYWFhYWFhYXGBgYGRkbGx0eHyEjJCYpKy0uMTM0Njg4Ojw9Pj9AQUJCQkNCQkFAQD8+Pj08Ozo5Nzc2NTQyMjEwLy8uLi0sLCsrKyoqKSkpKSgoKCgnJyYmJiUmJSUlJCQkJCQkJCMjIyMjIyMjIyMiIiIiIiIiISEhISEgICAgICAgHx8fHx8fHx8fHh4eHh4eHh4eHh0eHR0dHR0dHR0dHRwcHBwcHBwcHBsbGxsbGxsbGxsbGhsaGhoaGhoaGhoaGRoZGRkZGRkZGRkZGRkZGRkZGRkYGRgYGBgYGBgYGBgYGBgXGBcXFxcXFxcXFxcXFxcXFxcXFxcXFxcXFxcXFxcWFxYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFhYWFRYVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUVFRUUFRQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBQUFBMUFBMUFBMUFBMUFBMUFBMUFBMTFBMTFBMTFBMTFBMTFBMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTExMTEBMQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEA==');
    audio.play();
  };
  
  // Collapsible behavior removed: header no longer toggles anything
  
  // Start timer for this step
  const handleStartTimer = () => {
    // Reset remaining time to full duration before starting
    setRemainingTime(step.duration);
    setTimerRunning(true);
    setActiveStepId(step.id);
    
    // Track step start in analytics
    trackStepStart(step.id, step.name);
  };
  
  // Effect to handle when a step becomes inactive or active
  useEffect(() => {
    // If this step becomes active, expand it
    // Expanded constant (always true) now; retain any logic dependent on active/completed below
    
    // If this step is no longer active but the timer was running
    if (!isActive && timerRunning) {
      // Clear the timer and update states
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTimerRunning(false);
    }
    
    // If this step is marked as completed, ensure timer is stopped
    if (isCompleted && timerRunning) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTimerRunning(false);
      setRemainingTime(0);
      
      // Update parent component with time
      if (onTimeUpdate) {
        onTimeUpdate(0);
      }
    }
  }, [isActive, isCompleted, timerRunning, onTimeUpdate, expanded]);
  
  // Main effect for timer functionality
  useEffect(() => {
    // When step becomes active
    if (isActive && !timerRunning) {
      const initialTime = step.duration;
      setRemainingTime(initialTime);
      setTimerRunning(true);
  // expanded always true
      
      // Update parent component with initial time
      if (onTimeUpdate) {
        onTimeUpdate(initialTime);
      }
    }
    
    // Only run timer if this step is active AND timer should be running
    if (isActive && timerRunning) {
      // Clean up any existing timer first
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // Start a new timer
      timerRef.current = setInterval(() => {
        setRemainingTime(prev => {
          const newTime = prev <= 1 ? 0 : prev - 1;
          
          // Update parent component with time
          if (onTimeUpdate) {
            onTimeUpdate(newTime);
          }
          
          if (prev <= 1) {
            // Clear the timer
            clearInterval(timerRef.current);
            timerRef.current = null;
            
            // Play sound notification
            playNotificationSound();
            
            // Mark step as complete - notification handled in parent
            onStepComplete(step.id);
          }
          
          return newTime;
        });
      }, 1000);
    }
    
    // Cleanup on unmount or when dependencies change
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isActive, timerRunning, step, onStepComplete, onTimeUpdate]);
  
  // Get status class and text
  const getStatusInfo = () => {
    if (isCompleted) {
      return { class: 'completed', text: 'Completed' };
    } else if (isActive) {
      return { class: 'in-progress', text: 'In Progress' };
    } else {
      return { class: '', text: 'Not Started' };
    }
  };
  
  const statusInfo = getStatusInfo();
  const isWarning = remainingTime <= step.duration * 0.1 && timerRunning;
  
  return (
    <div 
      className={`step-item active ${isCompleted ? 'completed' : ''} ${isActive ? 'current' : ''}`}
      data-step={step.id}
    >
      {/* Compact summary row clickable to expand/collapse */}
      <div className="step-header">
        <div className="step-summary">
          <span className="step-number">{step.id}</span>
          <span className="step-title">{step.name}</span>
          <span className={`step-status ${statusInfo.class}`}>{statusInfo.text}</span>
          <span className="step-time-inline">
            {isCompleted ? 'Done' : formatTime(remainingTime)}
          </span>
        </div>
        {/* Toggle indicator removed for always-on layout */}
      </div>

      {/* Stacked content */}
      <div className="step-content">
        <div className="step-details">
          {step.details}
        </div>

        <div className="timer-section">
          <div className="countdown-container">
            <div className={`countdown ${isWarning ? 'warning' : ''}`}>
              {isCompleted ? '00:00:00' : formatTime(remainingTime)}
            </div>
          </div>
          <div className="timer-controls">
            <button
              onClick={handleStartTimer}
              disabled={timerRunning || isCompleted}
            >
              {timerRunning ? 'Timer running' : 'Start this step'}
            </button>
          </div>
        </div>

        <div className="next-action">
          <strong>Next:</strong> {step.nextAction}
        </div>
      </div>
    </div>
  );
}

export default RunbookStep;